import { useState, useEffect, useRef } from "react";
import { ShieldCheck, Heart, Award } from "lucide-react";
import { getProducts, DEFAULT_PRODUCTS } from "@/lib/content";
import type { Product } from "@/types/content";

const FeaturesSection = () => {
  const storeName = import.meta.env.VITE_STORE_NAME || "QYISSA";
  const [products, setProducts] = useState<Product[]>(DEFAULT_PRODUCTS.slice(0, 4));

  useEffect(() => {
    getProducts((fetched) => {
      if (fetched && fetched.length > 0) {
        setProducts(fetched.slice(0, 4));
      }
    }).then(fetched => {
      if (fetched && fetched.length > 0) {
        setProducts(fetched.slice(0, 4));
      }
    });
  }, []);

  return (
    <section className="bg-[#FAF8F5] border-t border-border/40">
      <div className="container px-4 md:px-8 py-20 md:py-28">
        {/* Header */}
        <div className="text-center mb-16 md:mb-24 space-y-4">
          <p className="text-muted-foreground font-body font-light tracking-[0.25em] uppercase text-[10px] md:text-xs">OUR PHILOSOPHY</p>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-display font-light text-foreground uppercase tracking-[0.3em] leading-relaxed">Artisanal Sourcing &<br />Conscious Design</h2>
          <p className="text-muted-foreground max-w-xl mx-auto text-xs sm:text-sm font-body font-light tracking-wide uppercase leading-relaxed">
            Timeless silhouettes crafted from premium natural fibers, designed to last a lifetime.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 sm:gap-10 max-w-7xl mx-auto px-1 sm:px-0">
          {/* Card 1: Safety Standards */}
          <div className="col-span-1 bg-[#FAF8F5] border border-border/50 p-8 md:p-12 flex flex-col items-center text-center rounded-none transition-colors hover:bg-foreground/[0.02] duration-500">
            <div className="w-12 h-12 mb-6 flex items-center justify-center border border-border/85 rounded-none text-foreground/75">
              <ShieldCheck size={20} strokeWidth={1.5} />
            </div>
            <h3 className="text-sm font-display font-light text-foreground mb-3 uppercase tracking-widest">Material Excellence</h3>
            <p className="text-xs text-muted-foreground leading-relaxed font-body font-light">
              We source only the finest raw fibers—including long-staple organic cotton, linen, and pure merino wool—that feel natural against the skin.
            </p>
          </div>

          {/* Card 2: Minimalist Design */}
          <div className="col-span-1 bg-[#FAF8F5] border border-border/50 p-8 md:p-12 flex flex-col items-center text-center rounded-none transition-colors hover:bg-foreground/[0.02] duration-500">
            <div className="w-12 h-12 mb-6 flex items-center justify-center border border-border/85 rounded-none text-foreground/75">
              <Heart size={20} strokeWidth={1.5} />
            </div>
            <h3 className="text-sm font-display font-light text-foreground mb-3 uppercase tracking-widest">Minimalist Design</h3>
            <p className="text-xs text-muted-foreground leading-relaxed font-body font-light">
              Guided by quiet luxury principles, our cuts focus on clean proportions, relaxed lines, and structural simplicity rather than temporary trends.
            </p>
          </div>

          {/* Card 3: Ethical Production */}
          <div className="col-span-1 bg-[#FAF8F5] border border-border/50 p-8 md:p-12 flex flex-col items-center text-center rounded-none transition-colors hover:bg-foreground/[0.02] duration-500">
            <div className="w-12 h-12 mb-6 flex items-center justify-center border border-border/85 rounded-none text-foreground/75">
              <Award size={20} strokeWidth={1.5} />
            </div>
            <h3 className="text-sm font-display font-light text-foreground mb-3 uppercase tracking-widest">Ethical Production</h3>
            <p className="text-xs text-muted-foreground leading-relaxed font-body font-light">
              We partner directly with small heritage mills and family-owned ateliers, ensuring ethical labor practices and fair wages for all craftsmen.
            </p>
          </div>
        </div>
        {/* Editorial Banner / Featured Product */}
        <div className="mt-16 md:mt-24 max-w-7xl mx-auto relative h-[350px] md:h-[500px] overflow-hidden flex items-center justify-center bg-[#171717] group cursor-pointer" onClick={() => document.getElementById('products-list')?.scrollIntoView({ behavior: 'smooth' })}>
          <img
            src={products[0]?.image || "https://images.unsplash.com/photo-1489987707023-afc15ba65ca0?q=80&w=2000&auto=format&fit=crop"}
            alt={products[0]?.name || "Editorial"}
            className="absolute inset-0 w-full h-full object-cover opacity-60 mix-blend-luminosity group-hover:scale-105 group-hover:opacity-80 transition-all duration-1000"
            loading="lazy"
          />
          {/* Subtle Gradient Overlay for Text Readability */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-60 group-hover:opacity-80 transition-opacity duration-500" />

          <div className="relative z-10 text-center px-6 transform group-hover:-translate-y-2 transition-transform duration-500">
            <span className="block text-[#EAE5D9]/70 font-body font-light text-[9px] md:text-xs uppercase tracking-[0.4em] mb-4 md:mb-6">
              {products[0]?.tag || "THE PHILOSOPHY"}
            </span>
            <h3 className="text-xl md:text-4xl text-[#EAE5D9] font-display font-light uppercase tracking-[0.25em] leading-snug drop-shadow-md">
              Elegance is refusal.
            </h3>
            <div className="mt-8 flex items-center justify-center opacity-0 group-hover:opacity-100 transform translate-y-4 group-hover:translate-y-0 transition-all duration-500">
              <span className="text-[#D4B892] text-[10px] md:text-xs uppercase tracking-[0.2em] font-bold border-b-2 border-transparent group-hover:border-[#D4B892] pb-1 transition-colors duration-300">
                Explore {products[0]?.name || "Collection"}
              </span>
            </div>
          </div>
        </div>

      </div>

      {/* Full-width Product Tiles Section */}
      <div className="w-full bg-[#121212] border-t-[8px] border-[#D4B892]">
        {products && products.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-3 w-full">
            {products.slice(1, 4).map((product, index) => (
              <div
                key={product.id || index}
                className="group cursor-pointer relative h-[50vh] md:h-[75vh] overflow-hidden"
                onClick={() => document.getElementById('products-list')?.scrollIntoView({ behavior: 'smooth' })}
              >
                {/* Image */}
                <div className="absolute inset-0 bg-[#161f19]">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover transition-transform duration-[1.5s] ease-[cubic-bezier(0.25,0.1,0.25,1.0)] group-hover:scale-105 opacity-90 group-hover:opacity-100"
                    loading="lazy"
                  />
                </div>

                {/* Subtle Gradient Overlay for Text Readability */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent opacity-70 group-hover:opacity-90 transition-opacity duration-500" />

                {/* Overlay Text Content (Bottom Left) */}
                <div className="absolute bottom-0 left-0 p-8 md:p-12 flex flex-col items-start z-10 w-full transform translate-y-4 group-hover:translate-y-0 transition-transform duration-500">
                  <h3 className="text-white font-display font-medium text-xl md:text-2xl uppercase tracking-widest mb-3 drop-shadow-md">
                    {product.name}
                  </h3>
                  <div className="flex items-center gap-2 text-white font-body text-[10px] md:text-xs font-bold uppercase tracking-[0.2em]">
                    <span className="border-b-2 border-transparent group-hover:border-white transition-colors duration-300 pb-1 drop-shadow-sm">
                      Shop Now
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default FeaturesSection;
